import React, { Suspense, useContext } from 'react';
import { Switch, withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { CommonStore, commonStore } from './stores';
import withTracking from 'tracking/withTracking';
import { startSentry } from 'errorReporting';
import { i18n } from '@lingui/core';

import {
    Spinner,
    NavigationApp,
    Route,
    RoutePrivate,
    RoutePublic,
    RouteRedirect,
    ScrollToTop,
    ErrorBoundary,
    Placeholder,
} from './components';
import { PATHS, URLS } from 'config';

import { str_slugify, lazyWithRetry } from './helpers';

import Register from './pages/Auth/Register';
import Login from './pages/Auth/Login';
import Access from './pages/Auth/Access';
import ConfirmEmail from './pages/Auth/ConfirmEmail';
import ProcessAppleSignIn from './pages/ProcessAppleSignIn';

startSentry();

const Main = ({ analytics, location, history }) => {
    commonStore.routeHistory = history;
    const store = useContext(CommonStore);

    if (!store.isAppReady) {
        return (
            <Spinner
                message={
                    store.appLoadingMessage
                        ? i18n._(store.appLoadingMessage)
                        : ''
                }
            />
        );
    }

    const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
    const Download = lazyWithRetry(() => import('./pages/Download'));
    const Plans = lazyWithRetry(() => import('./pages/Plans'));
    const Subscribe = lazyWithRetry(() => import('./pages/Subscribe/Plans'));
    const SubscribeCheckout = lazyWithRetry(() =>
        import('./pages/Subscribe/Checkout'),
    );
    const SubscribeWelcome = lazyWithRetry(() =>
        import('./pages/Subscribe/Success'),
    );

    const CampaignSuccess = lazyWithRetry(() =>
        import('./pages/CampaignSuccess'),
    );
    const NoAccess = lazyWithRetry(() => import('./pages/NoAccess'));
    const GiftingLanding = lazyWithRetry(() =>
        import('./pages/Gifting/Landing'),
    );
    const GiftingCustomize = lazyWithRetry(() =>
        import('./pages/Gifting/Customize'),
    );
    const GiftingCheckout = lazyWithRetry(() =>
        import('./pages/Gifting/Checkout'),
    );
    const GiftingSuccess = lazyWithRetry(() =>
        import('./pages/Gifting/Success'),
    );
    const GiftingRedeem = lazyWithRetry(() => import('./pages/Gifting/Redeem'));
    const GiftingRedeemSuccess = lazyWithRetry(() =>
        import('./pages/Gifting/RedeemSuccess'),
    );

    const Error404 = lazyWithRetry(() => import('./pages/Error404'));
    const PaymentError = lazyWithRetry(() => import('./pages/PaymentError'));
    const TokenAuth = lazyWithRetry(() => import('./pages/Auth/TokenAuth'));
    const WinbackLanding = lazyWithRetry(() =>
        import('./pages/Winback/Landing'),
    );
    const WinbackCheckout = lazyWithRetry(() =>
        import('./pages/Winback/Checkout'),
    );

    const TrialsLanding = lazyWithRetry(() => import('./pages/Trials/Landing'));
    const TrialsCheckout = lazyWithRetry(() =>
        import('./pages/Trials/Checkout'),
    );

    const RedeemCoupon = lazyWithRetry(() => import('./pages/Redeem/Coupon'));
    const SelectInstrument = lazyWithRetry(() =>
        import('./pages/SelectInstrument'),
    );

    const FreeTrialOfferLanding = lazyWithRetry(() =>
        import('./pages/FreeTrialOffer/Checkout'),
    );
    const FreeTrialOfferSuccess = lazyWithRetry(() =>
        import('./pages/FreeTrialOffer/Success'),
    );
    const FreeTrialOffer7Landing = lazyWithRetry(() =>
        import('./pages/FreeTrialOffer7/Checkout'),
    );

    const FreeTrialOffer14Landing = lazyWithRetry(() =>
        import('./pages/FreeTrialOffer14/Checkout'),
    );

    const BackToSchoolLanding = lazyWithRetry(() =>
        import('./pages/BackToSchool/Landing'),
    );
    const BackToSchoolCheckout = lazyWithRetry(() =>
        import('./pages/BackToSchool/Checkout'),
    );
    const BackToSchoolOver = lazyWithRetry(() =>
        import('./pages/BackToSchool/Over'),
    );

    const NewYearLanding = lazyWithRetry(() =>
        import('./pages/NewYear/Landing'),
    );
    const NewYearCheckout = lazyWithRetry(() =>
        import('./pages/NewYear/Checkout'),
    );
    const NewYearOver = lazyWithRetry(() => import('./pages/NewYear/Over'));

    const BlackFridayLanding = lazyWithRetry(() =>
        import('./pages/BlackFriday/Landing'),
    );
    const BlackFridayCheckout = lazyWithRetry(() =>
        import('./pages/BlackFriday/Checkout'),
    );
    const BlackFridayOver = lazyWithRetry(() =>
        import('./pages/BlackFriday/Over'),
    );

    const FamilyAddMemberLanding = lazyWithRetry(() =>
        import('./pages/FamilyAddMember'),
    );

    const AprilDiscountLanding = lazyWithRetry(() =>
        import('./pages/AprilDiscount/Landing'),
    );
    const AprilDiscountCheckout = lazyWithRetry(() =>
        import('./pages/AprilDiscount/Checkout'),
    );

    const Yousician25Landing = lazyWithRetry(() =>
        import('./pages/Yousician25/Landing'),
    );
    const Yousician25Checkout = lazyWithRetry(() =>
        import('./pages/Yousician25/Checkout'),
    );
    const Yousician25Over = lazyWithRetry(() =>
        import('./pages/Yousician25/Over'),
    );

    const PremiumOfferLanding = lazyWithRetry(() =>
        import('./pages/PremiumOffer/Landing'),
    );
    const PremiumOfferCheckout = lazyWithRetry(() =>
        import('./pages/PremiumOffer/Checkout'),
    );
    const PremiumOfferOver = lazyWithRetry(() =>
        import('./pages/PremiumOffer/Over'),
    );

    const PremiumPlusOfferLanding = lazyWithRetry(() =>
        import('./pages/PremiumPlusOffer/Landing'),
    );
    const PremiumPlusOfferCheckout = lazyWithRetry(() =>
        import('./pages/PremiumPlusOffer/Checkout'),
    );

    // world music day
    const WorldMusicDayLanding = lazyWithRetry(() =>
        import('./pages/WorldMusicDay/Landing'),
    );
    const WorldMusicDayCheckout = lazyWithRetry(() =>
        import('./pages/WorldMusicDay/Checkout'),
    );
    const WorldMusicDayOver = lazyWithRetry(() =>
        import('./pages/WorldMusicDay/Over'),
    );

    // upgrade
    const SubscriptionUpgrade = lazyWithRetry(() =>
        import('./pages/Upgrade/Plans'),
    );
    const SubscriptionUpgraded = lazyWithRetry(() =>
        import('./pages/Upgrade/Success'),
    );
    const SubscriptionUpgradeCheckout = lazyWithRetry(() =>
        import('./pages/Upgrade/Checkout'),
    );

    const SubscriptionCancel = lazyWithRetry(() =>
        import('./pages/SubscriptionCancel/Confirm'),
    );
    const SubscriptionCancelReason = lazyWithRetry(() =>
        import('./pages/SubscriptionCancel/Reason'),
    );
    const SubscriptionCanceled = lazyWithRetry(() =>
        import('./pages/SubscriptionCancel/Success'),
    );

    const ResubscribePlans = lazyWithRetry(() =>
        import('./pages/Resubscribe/Plans'),
    );
    const ResubscribeSuccess = lazyWithRetry(() =>
        import('./pages/Resubscribe/Success'),
    );
    const ResubscribeCheckout = lazyWithRetry(() =>
        import('./pages/Resubscribe/Checkout'),
    );
    const PurchasePage = lazyWithRetry(() => import('./pages/Purchase'));

    const DownloadData = lazyWithRetry(() => import('./pages/DownloadData'));
    const DeleteAccount = lazyWithRetry(() =>
        import('./pages/DeleteAccount/Confirm'),
    );
    const DeleteAccountConfirm = lazyWithRetry(() =>
        import('./pages/DeleteAccount/Feedback'),
    );

    const ResetPassword = lazyWithRetry(() =>
        import('./pages/Auth/ResetPassword'),
    );
    const ForgotPassword = lazyWithRetry(() =>
        import('./pages/Auth/ForgotPassword'),
    );
    const ProcessFacebookLogin = lazyWithRetry(() =>
        import('./pages/ProcessFacebookLogin'),
    );

    const ProcessFreeTrial = lazyWithRetry(() =>
        import('./pages/ProcessFreeTrial'),
    );
    const ProcessExternalService = lazyWithRetry(() =>
        import('./pages/ProcessExternalService'),
    );
    const PaypalProcess = lazyWithRetry(() => import('./pages/PaypalProcess'));

    const PartnershipJasonLanding = lazyWithRetry(() =>
        import('./pages/PartnershipJason/Landing'),
    );
    const PartnershipJasonCheckout = lazyWithRetry(() =>
        import('./pages/PartnershipJason/Checkout'),
    );
    const PartnershipJasonOver = lazyWithRetry(() =>
        import('./pages/PartnershipJason/Over'),
    );

    if (store.user && store.user.is_silent) {
        const DashboardNinja = lazyWithRetry(() =>
            import('./pages/DashboardNinja'),
        );
        // Ninja users can only delete their account and download
        // their data. Therefore we shouldn't show any pages
        // to them. In the future this could change, and
        // we might wanna show ways for them to claim
        // the account in the website. If that ever
        // happens, this return can be changed
        // into something that makes more
        // sense...
        return (
            <ErrorBoundary history={history}>
                <ScrollToTop>
                    <NavigationApp
                        analytics={analytics}
                        history={history}
                        location={location}
                    />
                    <Suspense fallback={<Placeholder />}>
                        <Switch>
                            {/* Only logged in users can access */}
                            <RoutePrivate
                                path={PATHS.dashboard}
                                exact
                                component={DashboardNinja}
                            />

                            {/* Redirects */}
                            <RouteRedirect
                                from="/account/profile"
                                to={PATHS.dashboard}
                            />

                            {/* Download your personal data */}
                            <RoutePrivate
                                path={PATHS.privacyDownload}
                                component={DownloadData}
                            />

                            {/* Delete your account */}
                            <RoutePrivate
                                path={PATHS.privacyDelete}
                                exact
                                component={DeleteAccount}
                            />
                            <Route
                                path={PATHS.privacyDeleteConfirm}
                                component={DeleteAccountConfirm}
                            />

                            {/* Public routes */}
                            <Route
                                path={`${PATHS.tokenAuth}/:token`}
                                component={TokenAuth}
                            />

                            {/**
                             * GT 'ninja' users need to confirm email
                             * before downloading user data
                             *
                             */}
                            <Route
                                path={PATHS.confirmEmail}
                                component={ConfirmEmail}
                            />

                            {/* The URL is not found */}
                            <Route component={Error404} />
                        </Switch>
                    </Suspense>
                </ScrollToTop>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary history={history}>
            <div
                className={
                    location
                        ? `page-${str_slugify(
                              location.pathname === '/'
                                  ? 'home'
                                  : location.pathname,
                          )}`
                        : ''
                }
            >
                <ScrollToTop>
                    <NavigationApp
                        analytics={analytics}
                        history={history}
                        location={location}
                    />

                    <Suspense fallback={<Placeholder />}>
                        <Switch>
                            {/* Only logged in users can access */}
                            <RoutePrivate
                                path={PATHS.dashboard}
                                exact
                                component={Dashboard}
                            />
                            <RoutePrivate
                                prefers="register"
                                path={PATHS.download}
                                exact
                                component={Download}
                            />

                            <Route
                                path={`${PATHS.plans}`}
                                exact
                                component={Plans}
                            />

                            {/* Subscribe */}
                            <RoutePrivate
                                path={PATHS.subscribe}
                                exact
                                component={Subscribe}
                            />
                            <RoutePrivate
                                path={`${PATHS.subscribeCheckout}/:step`}
                                prefers="register"
                                component={SubscribeCheckout}
                            />
                            <RoutePrivate
                                path={PATHS.subscribeWelcome}
                                component={SubscribeWelcome}
                            />
                            {/* Meaningful errors */}
                            <RoutePrivate
                                path={PATHS.noAccess}
                                component={NoAccess}
                            />
                            {/* Gifting */}
                            <Route
                                path={PATHS.gift}
                                exact
                                component={GiftingLanding}
                                accessCampaign={'gifting'}
                            />
                            <RoutePrivate
                                path={PATHS.giftCustomize}
                                exact
                                component={GiftingCustomize}
                                accessCampaign={'gifting'}
                            />
                            <RoutePrivate
                                path={PATHS.giftCheckout}
                                exact
                                component={GiftingCheckout}
                                accessCampaign={'gifting'}
                            />
                            <RoutePrivate
                                path={PATHS.giftSuccess}
                                component={GiftingSuccess}
                                accessCampaign={'gifting'}
                            />
                            <RoutePrivate
                                path={PATHS.giftRedeem}
                                exact
                                component={GiftingRedeem}
                                accessCampaign={'gifting'}
                            />
                            <RoutePrivate
                                path={PATHS.giftRedeemSuccess}
                                exact
                                component={GiftingRedeemSuccess}
                                accessCampaign={'gifting'}
                            />
                            {/* Winback 2021 campaign (COVID-19 Limited Edititon) */}
                            <Route
                                path={PATHS.aprilDiscount}
                                exact
                                component={AprilDiscountLanding}
                                accessCampaign={'april-discount'}
                            />
                            <RoutePrivate
                                path={PATHS.aprilDiscountCheckout}
                                exact
                                component={AprilDiscountCheckout}
                                accessCampaign={'april-discount'}
                            />
                            <RoutePrivate
                                path={PATHS.aprilDiscountSuccess}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'april-discount'}
                            />

                            {/* Black Friday */}
                            <Route
                                path={PATHS.blackFriday}
                                exact
                                component={BlackFridayLanding}
                                accessCampaign={'black-friday'}
                            />
                            <RoutePrivate
                                path={PATHS.blackFridayCheckout}
                                exact
                                component={BlackFridayCheckout}
                                accessCampaign={'black-friday'}
                            />
                            <RoutePrivate
                                path={PATHS.blackFridaySuccess}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'black-friday'}
                            />
                            <Route
                                path={PATHS.blackFridayOver}
                                exact
                                component={BlackFridayOver}
                                accessCampaign={'black-friday'}
                            />
                            {/* Yousician25 */}
                            <RoutePrivate
                                path={PATHS.yousician25}
                                exact
                                component={Yousician25Landing}
                                accessCampaign={'yousician-25'}
                            />
                            <RoutePrivate
                                path={PATHS.yousician25Checkout}
                                exact
                                component={Yousician25Checkout}
                                accessCampaign={'yousician-25'}
                            />
                            <RoutePrivate
                                path={PATHS.yousician25Success}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'yousician-25'}
                            />
                            <Route
                                path={PATHS.yousician25Over}
                                exact
                                component={Yousician25Over}
                                accessCampaign={'yousician-25'}
                            />
                            {/* Premium Offer */}
                            <RoutePrivate
                                path={PATHS.premiumOffer}
                                exact
                                component={PremiumOfferLanding}
                                accessCampaign={'premium-offer'}
                            />
                            <RoutePrivate
                                path={PATHS.premiumOfferCheckout}
                                exact
                                component={PremiumOfferCheckout}
                                accessCampaign={'premium-offer'}
                            />
                            <RoutePrivate
                                path={PATHS.premiumOfferSuccess}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'premium-offer'}
                            />
                            <Route
                                path={PATHS.premiumOfferOver}
                                exact
                                component={PremiumOfferOver}
                                accessCampaign={'premium-offer'}
                            />
                            {/* Premium Plus Offer */}
                            <RoutePrivate
                                path={PATHS.premiumPlusOffer}
                                exact
                                component={PremiumPlusOfferLanding}
                                accessCampaign={'premium-plus-offer'}
                            />
                            <RoutePrivate
                                path={PATHS.premiumPlusOfferCheckout}
                                exact
                                component={PremiumPlusOfferCheckout}
                                accessCampaign={'premium-plus-offer'}
                            />
                            <RoutePrivate
                                path={PATHS.premiumPlusOfferSuccess}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'premium-plus-offer'}
                            />

                            {/* World Music Day */}
                            <RoutePrivate
                                path={PATHS.worldMusicDay}
                                exact
                                component={WorldMusicDayLanding}
                                accessCampaign={'world-music-day'}
                            />
                            <RoutePrivate
                                path={PATHS.worldMusicDayCheckout}
                                exact
                                component={WorldMusicDayCheckout}
                                accessCampaign={'world-music-day'}
                            />
                            <RoutePrivate
                                path={PATHS.worldMusicDaySuccess}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'world-music-day'}
                            />
                            <Route
                                path={PATHS.worldMusicDayOver}
                                exact
                                component={WorldMusicDayOver}
                                accessCampaign={'world-music-day'}
                            />
                            {/* New Year*/}
                            <Route
                                path={PATHS.newYear}
                                exact
                                component={NewYearLanding}
                                accessCampaign={'new-year'}
                            />
                            <RoutePrivate
                                path={PATHS.newYearCheckout}
                                exact
                                component={NewYearCheckout}
                                accessCampaign={'new-year'}
                            />
                            <RoutePrivate
                                path={PATHS.newYearSuccess}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'new-year'}
                            />
                            <Route
                                path={PATHS.newYearOver}
                                exact
                                component={NewYearOver}
                            />
                            {/* Back to School */}
                            <RoutePrivate
                                path={PATHS.backToSchool}
                                exact
                                component={BackToSchoolLanding}
                                accessCampaign={'back-to-school'}
                            />
                            <RoutePrivate
                                path={PATHS.backToSchoolCheckout}
                                exact
                                component={BackToSchoolCheckout}
                                accessCampaign={'back-to-school'}
                            />
                            <RoutePrivate
                                path={PATHS.backToSchoolSuccess}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'back-to-school'}
                            />
                            <Route
                                path={PATHS.backToSchoolOver}
                                exact
                                component={BackToSchoolOver}
                            />
                            {/* Free Trial Offer */}
                            <RoutePrivate
                                path={PATHS.freeTrialOffer}
                                exact
                                component={FreeTrialOfferLanding}
                                accessCampaign={'free-trial-offer'}
                            />
                            <RouteRedirect
                                from={PATHS.freeTrialOfferSuccessNormalized}
                                to={PATHS.freeTrialOfferSuccess}
                            />
                            <RoutePrivate
                                path={PATHS.freeTrialOfferSuccess}
                                exact
                                component={FreeTrialOfferSuccess}
                            />
                            {/* Free Trial Offer 7 */}
                            <RoutePrivate
                                path={PATHS.freeTrialOffer7}
                                exact
                                component={FreeTrialOffer7Landing}
                                accessCampaign={'free-trial-offer-7'}
                            />
                            <RouteRedirect
                                from={PATHS.freeTrialOffer7SuccessNormalized}
                                to={PATHS.freeTrialOffer7Success}
                            />
                            <RoutePrivate
                                path={PATHS.freeTrialOffer7Success}
                                exact
                                component={FreeTrialOfferSuccess}
                            />
                            {/* Free Trial Offer 14 */}
                            <RoutePrivate
                                path={PATHS.freeTrialOffer14}
                                exact
                                component={FreeTrialOffer14Landing}
                                accessCampaign={'free-trial-offer-14'}
                            />
                            <RouteRedirect
                                from={PATHS.freeTrialOffer14SuccessNormalized}
                                to={PATHS.freeTrialOffer14Success}
                            />
                            <RoutePrivate
                                path={PATHS.freeTrialOffer14Success}
                                exact
                                component={FreeTrialOfferSuccess}
                            />
                            {/* Winback */}
                            <RoutePrivate
                                path={PATHS.winback}
                                exact
                                component={WinbackLanding}
                                accessCampaign={'start-now'}
                            />
                            <RoutePrivate
                                path={PATHS.winbackCheckout}
                                exact
                                component={WinbackCheckout}
                                accessCampaign={'start-now'}
                            />
                            <RoutePrivate
                                path={PATHS.winbackSuccess}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'start-now'}
                            />
                            {/* Partnership campaign with Jason Mraz */}
                            <Route
                                path={PATHS.partnershipJason}
                                exact
                                component={PartnershipJasonLanding}
                                accessCampaign={'partnership-jason'}
                            />
                            <RoutePrivate
                                path={PATHS.partnershipJasonCheckout}
                                exact
                                component={PartnershipJasonCheckout}
                                accessCampaign={'partnership-jason'}
                            />
                            <Route
                                path={PATHS.partnershipJasonOver}
                                exact
                                component={PartnershipJasonOver}
                                accessCampaign={'partnership-jason'}
                            />
                            <RoutePrivate
                                path={PATHS.partnershipJasonSuccess}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'partnership-jason'}
                            />
                            {/* Start Trials */}
                            <RoutePrivate
                                path={PATHS.trialsSuccess}
                                exact
                                component={CampaignSuccess}
                                accessCampaign={'start-trial'}
                            />
                            <RoutePrivate
                                path={PATHS.trials}
                                exact
                                component={TrialsLanding}
                                accessCampaign={'start-trial'}
                            />
                            <RoutePrivate
                                path={PATHS.trialsCheckout}
                                exact
                                component={TrialsCheckout}
                                accessCampaign={'start-trial'}
                            />
                            {/* Redeem */}
                            <RoutePrivate
                                path={PATHS.redeem}
                                exact
                                component={RedeemCoupon}
                                accessCampaign={'redeem'}
                            />
                            <Route
                                path={PATHS.selectInstrument}
                                exact
                                component={SelectInstrument}
                            />
                            {/* Upgrade */}
                            <RoutePrivate
                                path={PATHS.upgrade}
                                exact
                                component={SubscriptionUpgrade}
                            />
                            <RoutePrivate
                                path={PATHS.upgraded}
                                component={SubscriptionUpgraded}
                            />
                            <RoutePrivate
                                path={`${PATHS.upgradeCheckout}/:step`}
                                component={SubscriptionUpgradeCheckout}
                            />
                            {/* Cancel your subscription */}
                            <RoutePrivate
                                path={PATHS.subscriptionCancel}
                                exact
                                component={SubscriptionCancel}
                            />
                            <RoutePrivate
                                path={`${PATHS.subscriptionCancel}/:reason`}
                                component={SubscriptionCancelReason}
                            />
                            <RoutePrivate
                                path={PATHS.subscriptionCanceled}
                                component={SubscriptionCanceled}
                            />
                            {/* Resubscribe */}
                            <RoutePrivate
                                path={PATHS.resubscribe}
                                exact
                                component={ResubscribePlans}
                            />
                            <RoutePrivate
                                path={PATHS.resubscribeSuccess}
                                exact
                                component={ResubscribeSuccess}
                            />
                            <RoutePrivate
                                path={`${PATHS.resubscribeCheckout}/:step`}
                                component={ResubscribeCheckout}
                            />
                            <RoutePrivate
                                path={`${PATHS.purchase}/:duration/:type`}
                                component={PurchasePage}
                                prefers="register"
                            />
                            {/* Download your personal data */}
                            <RoutePrivate
                                path={PATHS.privacyDownload}
                                component={DownloadData}
                            />
                            {/* Delete your account */}
                            <RoutePrivate
                                path={PATHS.privacyDelete}
                                exact
                                component={DeleteAccount}
                            />
                            <Route
                                path={PATHS.privacyDeleteConfirm}
                                component={DeleteAccountConfirm}
                            />
                            {/* Family plan add members */}
                            <RoutePrivate
                                path={PATHS.addFamilyMember}
                                exact
                                component={FamilyAddMemberLanding}
                            />
                            {/* Only logged out users can access */}
                            <RoutePublic
                                path={PATHS.login}
                                exact
                                component={Login}
                            />
                            <RoutePublic
                                path={PATHS.register}
                                component={Register}
                            />
                            <RoutePublic
                                path={PATHS.access}
                                component={Access}
                            />
                            <RoutePublic
                                path={PATHS.forgotPassword}
                                component={ForgotPassword}
                            />
                            {/* Redirects */}
                            <RouteRedirect
                                from="/resetpassword"
                                to={PATHS.resetPassword}
                            />
                            <RouteRedirect from="/signup" to={PATHS.register} />
                            <RouteRedirect
                                from="/gift/thank-you"
                                to={PATHS.giftSuccess}
                            />
                            {/* Temporary solution to https://yousician.atlassian.net/browse/YS-16943 */}
                            <RouteRedirect
                                from="/account/externalservice"
                                to={PATHS.fromApp.externalService}
                            />
                            <RouteRedirect
                                from="/account/login"
                                to={PATHS.login}
                                withParams={{
                                    zendeskRedirect: true,
                                    return_to: URLS.support,
                                }}
                            />
                            <RouteRedirect
                                from="/account/profile"
                                to={PATHS.dashboard}
                            />
                            {/* redirect to yousician.com/privacy-notice */}
                            <Route
                                path="/privacy-notice"
                                component={() => {
                                    window.location.replace(URLS.privacyNotice);
                                    return null;
                                }}
                            />
                            <RouteRedirect
                                from="/subscriptionsuccessful"
                                to={PATHS.subscribeWelcome}
                            />
                            <RouteRedirect
                                from={PATHS.subscribeSuccess}
                                to={PATHS.subscribeWelcome}
                            />
                            <RouteRedirect
                                from="/freetriallogin"
                                to={PATHS.access}
                                withParams={{
                                    campaign: 'free-trial-login',
                                    continue: `${PATHS.subscribe}?campaign=free-trial-login`,
                                }}
                            />
                            <RouteRedirect
                                from="/facebooklogin"
                                to="/facebook-login"
                            />
                            <RouteRedirect
                                from="/editpaymentmethod"
                                to={PATHS.dashboard}
                                withParams={{
                                    'update-payment-method': true,
                                }}
                            />
                            {/* Public routes */}
                            <Route
                                path={PATHS.fromApp.facebookLogin}
                                component={ProcessFacebookLogin}
                            />
                            <Route
                                path={PATHS.processAppleSignIn}
                                component={ProcessAppleSignIn}
                            />
                            <Route
                                path={PATHS.fromApp.freeTrial}
                                component={ProcessFreeTrial}
                            />
                            <Route
                                path={PATHS.fromApp.purchaseSubscription}
                                component={ProcessFreeTrial}
                            />
                            <Route
                                path={PATHS.fromApp.externalService}
                                component={ProcessExternalService}
                            />
                            <Route
                                path={PATHS.paypalProcess}
                                component={PaypalProcess}
                            />
                            <Route
                                path={PATHS.resetPassword}
                                component={ResetPassword}
                            />
                            <Route
                                path={PATHS.confirmEmail}
                                component={ConfirmEmail}
                            />
                            <Route
                                path={`${PATHS.tokenAuth}/:token`}
                                component={TokenAuth}
                            />
                            {/* Payment error page*/}
                            <RoutePrivate
                                path={PATHS.paymentFailed}
                                component={PaymentError}
                            />
                            {/* The URL is not found */}
                            <Route component={Error404} />
                        </Switch>
                    </Suspense>
                </ScrollToTop>
            </div>
        </ErrorBoundary>
    );
};

export default withTracking(withRouter(observer(Main)));
